import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CalendarCheck, Loader2, CheckCircle2 } from "lucide-react";

import { ContactTimePicker } from "./ContactTimePicker";

const blank = (city, state) => ({ name: "", email: "", phone: "", city: city || "", state: state || "", preferred_date: "", preferred_time: "", notes: "", website: "" });

const BookCallForm = ({ city, state, onDone }) => {
  const [f, setF] = useState(blank(city, state));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/call-requests`, { ...f, page: window.location.pathname });
      onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => `${x.loc?.slice(-1)[0]}: ${x.msg}`).join("; ") : d || "Couldn't send your request");
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="book-call-form">
      <div className="sm:col-span-2"><Label>Full name *</Label><Input required value={f.name} onChange={set("name")} data-testid="book-call-name" /></div>
      <div><Label>Phone *</Label><Input required type="tel" value={f.phone} onChange={set("phone")} data-testid="book-call-phone" /></div>
      <div><Label>Email *</Label><Input required type="email" value={f.email} onChange={set("email")} data-testid="book-call-email" /></div>
      <div><Label>City you're interested in</Label><Input value={f.city} onChange={set("city")} data-testid="book-call-city" /></div>
      <div><Label>State</Label><Input value={f.state} onChange={set("state")} data-testid="book-call-state" /></div>
      <div className="sm:col-span-2"><ContactTimePicker required value={{ date: f.preferred_date, time: f.preferred_time }}
        onChange={(v) => setF({ ...f, preferred_date: v.date, preferred_time: v.time })} /></div>
      <div className="sm:col-span-2"><Label>Anything we should know?</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="book-call-notes" /></div>
      <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <Button type="submit" disabled={busy} className="sm:col-span-2 bg-teal hover:bg-teal-dark text-white" data-testid="book-call-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request my free call"}
      </Button>
      <p className="sm:col-span-2 text-xs text-slate-500">Free 30-minute call, no obligation. We'll confirm the exact time by phone or email, usually within one business day.</p>
    </form>
  );
};

export const BookCallButton = ({ city, state, className = "", variant = "solid", label = "Book a free call" }) => {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const styles = variant === "outline"
    ? "border-2 border-white/70 text-white hover:bg-white hover:text-navy"
    : "bg-white text-navy hover:bg-teal hover:text-white";
  return (
    <>
      <button type="button" onClick={() => { setDone(false); setOpen(true); }} data-testid="book-call-btn"
        className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold font-body transition-colors ${styles} ${className}`}>
        <CalendarCheck className="w-5 h-5" /> {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="book-call-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy">Book a free call</DialogTitle>
            <DialogDescription>Talk with our team about how to open a Total Wireless store and which ready-to-go stores are available near you.</DialogDescription>
          </DialogHeader>
          {done ? (
            <div className="py-8 text-center space-y-2" data-testid="book-call-success">
              <CheckCircle2 className="w-12 h-12 text-teal mx-auto" />
              <p className="font-heading font-semibold text-navy text-lg">Request received!</p>
              <p className="text-sm text-slate-600">Our team will reach out to confirm your call time.</p>
            </div>
          ) : <BookCallForm city={city} state={state} onDone={() => setDone(true)} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
